import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  GraduationCap, ArrowLeft, CheckCircle, XCircle, Clock, 
  BookOpen, TrendingUp, Calendar, Filter, ChevronLeft, ChevronRight,
  Flame, Award, Target
} from "lucide-react";
import { toast } from "sonner";

export default function AssessmentHistory() {
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [subjectBreakdown, setSubjectBreakdown] = useState([]);
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ skip: 0, limit: 20, total: 0 });
  const [filters, setFilters] = useState({ subject: "", isCorrect: null });
  const [availableSubjects, setAvailableSubjects] = useState([]);

  // Fetch student data
  useEffect(() => {
    const fetchStudent = async () => {
      try {
        const res = await fetch(`${API}/auth/me`, { credentials: "include" });
        if (!res.ok) {
          navigate("/");
          return;
        }
        const data = await res.json();
        if (data.user) {
          setStudent(data.user);
        } else {
          navigate("/");
        }
      } catch {
        navigate("/");
      }
    };
    fetchStudent();
  }, [navigate]);

  // Fetch assessment history
  useEffect(() => {
    if (!student) return;
    
    const fetchAssessments = async () => {
      setLoading(true);
      try {
        let url = `${API}/assessments/student/${student.id}/history?skip=${pagination.skip}&limit=${pagination.limit}`;
        if (filters.subject) url += `&subject=${filters.subject}`;
        if (filters.isCorrect !== null) url += `&is_correct=${filters.isCorrect}`;
        
        const res = await fetch(url);
        const data = await res.json();
        
        if (data.success) {
          setAssessments(data.assessments);
          setSummary(data.summary);
          setSubjectBreakdown(data.subject_breakdown);
          setPagination(prev => ({ ...prev, total: data.pagination.total }));
          
          // Extract unique subjects
          const subjects = data.subject_breakdown.map(s => s.subject);
          setAvailableSubjects(subjects);
        }
      } catch (error) {
        toast.error("Failed to load assessment history");
      } finally {
        setLoading(false);
      }
    };
    
    fetchAssessments();
  }, [student, pagination.skip, pagination.limit, filters]);

  // Fetch streak data
  useEffect(() => {
    if (!student) return;
    
    const fetchStreak = async () => {
      try {
        const res = await fetch(`${API}/assessments/student/${student.id}/streak`);
        const data = await res.json();
        if (data.success) {
          setStreak(data);
        }
      } catch (error) {
        console.error("Failed to fetch streak:", error);
      }
    };
    
    fetchStreak();
  }, [student]);

  const handlePageChange = (direction) => {
    setPagination(prev => ({
      ...prev,
      skip: direction === "next" 
        ? prev.skip + prev.limit 
        : Math.max(0, prev.skip - prev.limit)
    }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getAccuracyColor = (accuracy) => {
    if (accuracy >= 80) return "text-green-600";
    if (accuracy >= 60) return "text-yellow-600";
    return "text-red-600";
  };

  const getAccuracyBg = (accuracy) => {
    if (accuracy >= 80) return "bg-green-100";
    if (accuracy >= 60) return "bg-yellow-100";
    return "bg-red-100";
  };

  if (!student) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 py-4 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div 
            className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => navigate("/")}
            data-testid="eduzy-header-logo"
          >
            <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center">
              <GraduationCap className="h-6 w-6 text-white" />
            </div>
            <div>
              <span className="text-xl font-bold text-gray-900">Eduzy</span>
              <p className="text-xs text-gray-500">Assessment History</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            onClick={() => navigate("/student/profile")}
            className="text-gray-600"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Profile
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Assessment History</h1>
          <p className="text-gray-600">Track your learning progress and review past questions</p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {/* Total Questions */}
          <Card>
            <CardContent className="p-4 text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <BookOpen className="h-6 w-6 text-blue-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">{summary?.total_questions || 0}</p>
              <p className="text-sm text-gray-500">Total Questions</p>
            </CardContent>
          </Card>

          {/* Accuracy */}
          <Card>
            <CardContent className="p-4 text-center">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 ${getAccuracyBg(summary?.accuracy_percent || 0)}`}>
                <Target className={`h-6 w-6 ${getAccuracyColor(summary?.accuracy_percent || 0)}`} />
              </div>
              <p className={`text-2xl font-bold ${getAccuracyColor(summary?.accuracy_percent || 0)}`}>
                {summary?.accuracy_percent || 0}%
              </p>
              <p className="text-sm text-gray-500">Accuracy</p>
            </CardContent>
          </Card>

          {/* Current Streak */}
          <Card>
            <CardContent className="p-4 text-center">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Flame className="h-6 w-6 text-orange-600" />
              </div>
              <p className="text-2xl font-bold text-orange-600">{streak?.current_streak || 0}</p>
              <p className="text-sm text-gray-500">Day Streak</p>
            </CardContent>
          </Card>

          {/* Longest Streak */}
          <Card>
            <CardContent className="p-4 text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Award className="h-6 w-6 text-purple-600" />
              </div>
              <p className="text-2xl font-bold text-purple-600">{streak?.longest_streak || 0}</p>
              <p className="text-sm text-gray-500">Best Streak</p>
            </CardContent>
          </Card>
        </div>

        {/* Subject Breakdown */}
        {subjectBreakdown.length > 0 && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-emerald-600" />
                Subject Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {subjectBreakdown.map((subject) => (
                  <div 
                    key={subject.subject}
                    className="bg-gray-50 rounded-lg p-4"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900">{subject.subject}</span>
                      <span className={`text-sm font-semibold ${getAccuracyColor(subject.accuracy)}`}>
                        {subject.accuracy}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                      <div 
                        className={`h-2 rounded-full ${subject.accuracy >= 80 ? 'bg-green-500' : subject.accuracy >= 60 ? 'bg-yellow-500' : 'bg-red-500'}`}
                        style={{ width: `${subject.accuracy}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-500">
                      {subject.correct} / {subject.total} correct
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-gray-400" />
                <span className="text-sm font-medium text-gray-700">Filters:</span>
              </div>
              
              {/* Subject Filter */}
              <select
                value={filters.subject}
                onChange={(e) => {
                  setFilters(prev => ({ ...prev, subject: e.target.value }));
                  setPagination(prev => ({ ...prev, skip: 0 }));
                }}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">All Subjects</option>
                {availableSubjects.map(subj => (
                  <option key={subj} value={subj}>{subj}</option>
                ))}
              </select>

              {/* Correct/Incorrect Filter */}
              <select
                value={filters.isCorrect === null ? "" : filters.isCorrect.toString()}
                onChange={(e) => {
                  const val = e.target.value;
                  setFilters(prev => ({ 
                    ...prev, 
                    isCorrect: val === "" ? null : val === "true" 
                  }));
                  setPagination(prev => ({ ...prev, skip: 0 }));
                }}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">All Answers</option>
                <option value="true">Correct Only</option>
                <option value="false">Incorrect Only</option>
              </select>

              {/* Clear Filters */}
              {(filters.subject || filters.isCorrect !== null) && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => {
                    setFilters({ subject: "", isCorrect: null });
                    setPagination(prev => ({ ...prev, skip: 0 }));
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Assessment List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-emerald-600" />
                Recent Questions
              </span>
              <span className="text-sm font-normal text-gray-500">
                {pagination.total} total
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
              </div>
            ) : assessments.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No assessments found</p>
                <p className="text-sm mt-1">Start learning on WhatsApp to see your progress here!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {assessments.map((assessment, index) => (
                  <div 
                    key={assessment.id || index}
                    className={`border rounded-lg p-4 ${assessment.is_correct ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {assessment.is_correct ? (
                            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                          ) : (
                            <XCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                          )}
                          <span className={`text-sm font-medium ${assessment.is_correct ? 'text-green-700' : 'text-red-700'}`}>
                            {assessment.is_correct ? 'Correct' : 'Incorrect'}
                          </span>
                          {assessment.subject && (
                            <span className="px-2 py-0.5 bg-gray-200 text-gray-700 text-xs rounded-full">
                              {assessment.subject}
                            </span>
                          )}
                        </div>
                        
                        <p className="text-gray-900 font-medium mb-2">{assessment.question}</p>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                          <div className="flex items-start gap-2">
                            <span className="text-gray-500">Your answer:</span>
                            <span className={assessment.is_correct ? 'text-green-700' : 'text-red-700'}>
                              {assessment.student_answer || "No answer"}
                            </span>
                          </div>
                          {!assessment.is_correct && assessment.correct_answer && (
                            <div className="flex items-start gap-2">
                              <span className="text-gray-500">Correct answer:</span>
                              <span className="text-green-700">{assessment.correct_answer}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="text-right text-xs text-gray-500">
                        <Calendar className="h-3 w-3 inline-block mr-1" />
                        {formatDate(assessment.created_at)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {pagination.total > pagination.limit && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange("prev")}
                  disabled={pagination.skip === 0}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                
                <span className="text-sm text-gray-500">
                  Showing {pagination.skip + 1} - {Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total}
                </span>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange("next")}
                  disabled={pagination.skip + pagination.limit >= pagination.total}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="text-center py-6 text-sm text-gray-500 space-y-2">
        <p>Powered by Eduzy - Learn anywhere, anytime</p>
        <p>&copy; 2025 Eduzy by Contentium Tech</p>
      </footer>
    </div>
  );
}
